import { UserCircle2 } from 'lucide-react';
import { useAvatarSrc } from '../../../hooks/useAvatarDirectory';
import { useAuthStore } from '../../../store/useAuthStore';

/**
 * CLE-174 — initials avatar with a stable per-name colour (hashed) like Linear.
 * Unassigned renders a muted outline circle with a generic glyph. When the
 * person has a profile photo (Profil → Poza de profil) it is drawn instead of
 * the initials: passed as `src`, or found by `userId` / `name` in the team
 * directory (hooks/useAvatarDirectory).
 */

const PALETTE = [
  '#ef4444',
  '#f97316',
  '#eab308',
  '#22c55e',
  '#14b8a6',
  '#3b82f6',
  '#6366f1',
  '#8b5cf6',
  '#ec4899',
];

export function avatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return PALETTE[hash % PALETTE.length];
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('') || '?';
}

interface AvatarProps {
  name: string | null;
  size?: number;
  userId?: string | null;
  src?: string | null;
}

export function Avatar({ name, size = 24, userId, src }: AvatarProps) {
  const photo = useAvatarSrc({ src, userId, name });
  if (name && photo) {
    return (
      <img
        src={photo}
        alt={name}
        title={name}
        className="inline-block shrink-0 rounded-full object-cover"
        style={{ width: size, height: size }}
      />
    );
  }
  if (!name) {
    return (
      <span
        className="inline-flex items-center justify-center rounded-full text-gray-500"
        style={{ width: size, height: size }}
        aria-label="Neatribuit"
      >
        <UserCircle2 size={size} strokeWidth={1.25} className="opacity-60" />
      </span>
    );
  }

  return (
    <span
      className="inline-flex items-center justify-center rounded-full font-medium text-fixed-white"
      style={{
        width: size,
        height: size,
        backgroundColor: avatarColor(name),
        fontSize: size * 0.42,
      }}
      title={name}
      aria-label={name}
    >
      {initials(name)}
    </span>
  );
}

/** The signed-in person's avatar (comment composers, own chat bubble): photo from the session, no lookup. */
export function MeAvatar({ size = 24 }: { size?: number }) {
  const me = useAuthStore((s) => s.user);
  return <Avatar name={me?.name ?? null} userId={me?.id} src={me?.avatarUrl} size={size} />;
}
